import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rrFetch } from "@/lib/rehearsalRoomAuth";

/**
 * Master v5 Prompt 9 — TanStack Query hooks for Practice/Assignment/
 * InteractionInstance and the shared Rehearsal Room Runtime resolver.
 * Mirrors workDocumentQueries.js's `request()` error contract exactly
 * (`.status`/`.body` on thrown errors) so callers can branch on 403/404/409.
 */
async function request(path, options = {}) {
  const res = await rrFetch(path, options);
  if (res.status === 204) return null;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((body && (body.detail?.error || body.detail?.message || body.detail)) || "request_failed");
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

const jsonPost = (path, payload) => request(path, { method: "POST", body: JSON.stringify(payload ?? {}) });

export const pqk = {
  assignments: (wrkId, layerKey, perId) => ["practice-assignments", wrkId, layerKey, perId],
  instance: (wrkId, iinId) => ["interaction-instance", wrkId, iinId],
  room: (wrkId, launchId) => ["room-launch", wrkId, launchId],
  attempts: (wrkId, iinId) => ["interaction-attempts", wrkId, iinId],
};

export function usePracticeAssignments(workspaceId, layerKey, personId) {
  return useQuery({
    queryKey: pqk.assignments(workspaceId, layerKey, personId),
    enabled: !!workspaceId && !!layerKey && !!personId,
    retry: false,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/practice/${layerKey}/assignments`),
  });
}

export function useRequestPractice(workspaceId, layerKey) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (needNote) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/practice/${layerKey}/request`, { need_note: needNote || null }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["practice-assignments", workspaceId, layerKey] }),
  });
}

export function useStartAssignment(workspaceId) {
  return useMutation({
    mutationFn: (asgId) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/assignments/${asgId}/start`),
  });
}

export function useRoomLaunch(workspaceId, launchId) {
  return useQuery({
    queryKey: pqk.room(workspaceId, launchId),
    enabled: !!workspaceId && !!launchId,
    retry: false,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/room/${launchId}`),
  });
}

export function useInteractionInstance(workspaceId, iinId) {
  return useQuery({
    queryKey: pqk.instance(workspaceId, iinId),
    enabled: !!workspaceId && !!iinId,
    retry: false,
    refetchInterval: 8000, // mirrors useWorkDocumentDetail's own polling fallback
    queryFn: () => request(`/api/rehearsal-room/interaction-instances/${iinId}?wrk_id=${workspaceId}`),
  });
}

export function useSubmitInstance(workspaceId, iinId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => jsonPost(`/api/rehearsal-room/interaction-instances/${iinId}/submit?wrk_id=${workspaceId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pqk.instance(workspaceId, iinId) });
      // rehearsal_recorder's own renderer subscribes to this key (see
      // rehearsalQueries.js) — submitting is what triggers its
      // transcription/metrics/reflection panels to appear; a no-op for
      // every other renderer since nothing subscribes to this key there.
      qc.invalidateQueries({ queryKey: ["rehearsal-by-instance", workspaceId] });
    },
  });
}

export function useCompleteInstance(workspaceId, iinId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => jsonPost(`/api/rehearsal-room/interaction-instances/${iinId}/complete?wrk_id=${workspaceId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: pqk.instance(workspaceId, iinId) }),
  });
}

export function useReopenInstance(workspaceId, iinId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => jsonPost(`/api/rehearsal-room/interaction-instances/${iinId}/reopen?wrk_id=${workspaceId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pqk.instance(workspaceId, iinId) });
      qc.invalidateQueries({ queryKey: pqk.attempts(workspaceId, iinId) });
    },
  });
}

export function useInstanceAttempts(workspaceId, iinId) {
  return useQuery({
    queryKey: pqk.attempts(workspaceId, iinId),
    enabled: !!workspaceId && !!iinId,
    queryFn: () => request(`/api/rehearsal-room/interaction-instances/${iinId}/attempts?wrk_id=${workspaceId}`),
  });
}

export function useCreateReflection(workspaceId, iinId) {
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/interaction-instances/${iinId}/reflections?wrk_id=${workspaceId}`, payload),
  });
}

export function useCreateCommitment(workspaceId, iinId) {
  return useMutation({
    mutationFn: (text) => jsonPost(`/api/rehearsal-room/interaction-instances/${iinId}/commitments?wrk_id=${workspaceId}`, { instance_id: iinId, text }),
  });
}

// Master v5 Prompt 23 — closes the loop from Home's "one commitment" card.
export function useMarkCommitmentDone(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cmtId) => jsonPost(`/api/rehearsal-room/commitments/${cmtId}/done?wrk_id=${workspaceId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rehearsal-room", "home", workspaceId] }),
  });
}

// -- Room exit: called directly (not via useMutation) by RoomShell so it
// can fire-and-await it AFTER flushing every open block lease, never before.
export async function exitRoom(workspaceId, launchId, lastSafeRoute) {
  return jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/room/${launchId}/exit`, { last_safe_route: lastSafeRoute || null });
}

// Master v5 Prompt 18 — the coach's minimal "assign practice" picker
// inside Complete Session. Only published items, coach-only.
// Clients Restructure — Team 360's "+ Assign Practice"/"Add from
// Library". owner_member_id is always null here (shared/team-visible,
// see practice_contracts.Assignment) — an individual assignment stays
// Client 360's own job.
export function useCreateAssignment(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/assignments`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team-record", "practice", workspaceId] });
      qc.invalidateQueries({ queryKey: ["team-record", "files", workspaceId] });
      qc.invalidateQueries({ queryKey: ["team-record", "timeline", workspaceId] });
    },
  });
}

export function useLibraryCatalog(workspaceId) {
  return useQuery({
    queryKey: ["library-catalog", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/library-catalog`),
  });
}

// Master v5 Prompt 20 — Client Practice overview (Understand vs Practice)
// + a member's own assignment Favorites. Never exposes the raw Library.
export function usePracticeOverview(workspaceId) {
  return useQuery({
    queryKey: ["practice-overview", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/practice-overview`),
  });
}

export function useToggleAssignmentFavorite(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (asgId) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/assignments/${asgId}/favorite`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["practice-overview", workspaceId] });
      qc.invalidateQueries({ queryKey: ["favorite-assignments", workspaceId] });
    },
  });
}

export function useFavoriteAssignments(workspaceId) {
  return useQuery({
    queryKey: ["favorite-assignments", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/favorites/assignments`),
  });
}
